import { YStack, XStack, Form, Text } from "tamagui";
import { createPortal } from "react-dom";
import { OTPInputBlocks } from "@components/form/OTPInputBlocks";
import { containerStyle } from "@styles/index";
import { colors } from "@styles/colors";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { handleRequest } from "@services/axios/apiManager";
import { AxiosResponse } from "axios";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import { useUser } from "@utils/store/user/useUser";
import { useForm, Controller, useWatch } from "react-hook-form"; // 這裡不需要 useFormContext 因為你在同一層
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import getURLSearchParam from "@utils/func/getURLSearchParam";
import Cookies from "js-cookie";
import { addHours } from "date-fns";
import liff from "@line/liff";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import RadiusCard from "@components/layout/RadiusCard";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import { RiCoupon2Line } from "react-icons/ri";
import { track } from "@lib/tracker";
import { isPracticeMode } from "@utils/practice/practiceMode";
import ScanViewport from "@screen/Onboarding/components/ScanViewport";
import toast from "react-hot-toast";
import { toToastMessage } from "@utils/toastMessage";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { rememberPreferredContext } from "@utils/line/preferredContext";
import { clearFamilyInviteCode } from "@utils/familyInvite";
import { clearShopInviteCode } from "@utils/shopInviteCode";
import { clearReferredCourierId } from "@utils/referredCourier";
import { fetchCustomerContexts } from "@screen/Onboarding/registerApi";
import { pickChooseIdentity } from "@utils/func/identity";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import type { IdentityContext } from "@utils/store/user/useUser";
import { useAuthReady } from "@provider/LineLoginProvider";
import { buildRegisterBySerialBody, ALREADY_REGISTERED_MSG, RegisterBySerialResult } from "../api/registerBySerial";

const InvitedCode = () => {
  // 練習模式：正常綁定前先模擬「相機掃描客戶序號 QR」；掃描動畫跑約 2 秒自動完成或略過後才進序號輸入。
  const [scanDone, setScanDone] = useState(false);
  const showScan = isPracticeMode() && !scanDone;
  // 模擬掃描：進入掃描畫面約 2 秒後自動辨識成功 → 進序號輸入（離開/略過會清掉 timer）。
  useEffect(() => {
    if (!showScan) return;
    const t = setTimeout(() => setScanDone(true), 2200);
    return () => clearTimeout(t);
  }, [showScan]);
  const { lineAccessToken } = useLineAuth();
  const { setLoading } = useUtilsStore();
  const { setInvGasData, setChooseIdentity, setUserData, setAccountPhoneLoaded } = useUser();
  const setContactPhone = useKiosk((s) => s.setContactPhone);
  const { setCusJwtToken, cusJwtToken } = useCusAuth();
  // 開機時 Provider 會問後端「這個 LINE 帳號註冊過了沒」（/line/login）。它還沒回來前不可以送序號：
  // 兩件事並行時，主機冷開機讓 /line/login 晚回來，會拿開機當下的網址把剛註冊好、已在首頁的人
  // 拉去「客戶序號配對」（2026-10-15 dev 實測，見 bug-first-login-serial-redirect.md）。
  // authReady 在 200／401／失敗／練習模式都會變 true，不會卡死。
  const authReady = useAuthReady();
  const navigate = useNavigate();
  const [thisInviteCode, setThisInviteCode] = useState<string | null>(null);
  // 送出中：「下一步」按鈕顯示驗證中並停用，避免自動送出與手動按下重複打 API。
  const [submitting, setSubmitting] = useState(false);

  //@ 1. URL / sessionStorage / Cookie 任一來源 → 帶入邀請碼
  useEffect(() => {
    const inviteCode = getURLSearchParam("invite") || sessionStorage.getItem("invite") || Cookies.get("invite");
    if (inviteCode) {
      Cookies.set("invite", inviteCode, {
        expires: addHours(new Date(), 24), //過期日(當下 + 24hr)
        path: "/",
        secure: true,
        sameSite: "Lax",
      });
      sessionStorage.setItem("invite", inviteCode);
      setThisInviteCode(inviteCode);
    }
  }, []);

  //@ 2. Resume：使用者跳離後回來時，URL/storage/cookie 可能被 LIFF / Safari ITP 清掉。
  //   問 BE 有沒有上次留下的 binding（spec app-line-login-phone-otp-spec.md §4.3）。
  //   背景靜默呼叫、不顯示 spinner；找到就同步寫回 storage/cookie + state。
  useEffect(() => {
    if (!lineAccessToken) return;
    const localInvite = getURLSearchParam("invite") || sessionStorage.getItem("invite") || Cookies.get("invite");
    if (localInvite) return; // 本地已有邀請碼，不需要問 BE

    handleRequest({
      asyncRequest: () =>
        api.post(
          "/app-api/line/authentication-code-check",
          { accessToken: lineAccessToken },
          { isAuth: false },
        ),
      successCallback: (res: AxiosResponse) => {
        const data = res.data?.data;
        if (data?.hasBinding && data?.authenticationCode) {
          Cookies.set("invite", data.authenticationCode, {
            expires: addHours(new Date(), 24),
            path: "/",
            secure: true,
            sameSite: "Lax",
          });
          sessionStorage.setItem("invite", data.authenticationCode);
          setThisInviteCode(data.authenticationCode);
        }
      },
      errorCallback: () => {}, // 靜默：使用者仍可手動輸入
    });
  }, [lineAccessToken]);

  //@ 建立 Form
  const formSchema = z.object({
    invCode: z.string().trim().min(1, { message: "請填寫客戶序號" }), //-- 必填設定
  });

  type FormData = z.infer<typeof formSchema>;
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      invCode: "", // 這裡設定可預設值
    },
  });
  /**
   * 序號驗證成功 → 直接建帳進首頁（2026-10-14 拍板：不再經過手機驗證與基本資料頁；
   * 手機在第一次結帳由結算頁驗證）。寫 token 的方式同簡訊邀請落地頁（SmsInvite）。
   */
  const registerAndGoHome = (verifiedTicket?: string) => {
    if (!lineAccessToken || !verifiedTicket) {
      setSubmitting(false);
      return;
    }
    handleRequest({
      asyncRequest: () =>
        api.post("/app-api/line/register-by-serial", buildRegisterBySerialBody(lineAccessToken, verifiedTicket), { isAuth: false }),
      successCallback: async (res: AxiosResponse) => {
        const data = (res?.data?.data ?? {}) as RegisterBySerialResult;
        track("funnel_step", { funnel: "register", step: "register_complete" });
        Cookies.remove("invite");
        sessionStorage.removeItem("invite");
        clearFamilyInviteCode();
        clearShopInviteCode();
        clearReferredCourierId();
        // 練習站：mock 回 redirectUrl → 導回練習完成頁（留在沙盒）
        if (data.redirectUrl) {
          window.location.href = data.redirectUrl;
          return;
        }
        if (data.jwtToken) {
          sessionStorage.setItem("jwtToken", data.jwtToken);
          setCusJwtToken(data.jwtToken);
          // 刪帳號後重註冊：sessionStorage 留著舊人的身分／帳號資料 → 首頁帶舊 X-Context 403 卡住約 10 秒。
          // 先清掉舊資料，再用新 jwt 重抓身分，完成後才導首頁。
          setUserData(null);
          setAccountPhoneLoaded(false);
          setContactPhone("");
          try {
            const ctxRes = await fetchCustomerContexts(data.jwtToken);
            const d = ctxRes.data?.data;
            const contexts = (d?.contexts ?? []) as IdentityContext[];
            const preferred = contexts.find((c) => String(c.context_id) === String(data.context_id));
            const next =
              preferred ?? pickChooseIdentity(contexts, { currentChooseIdentity: null, defaultContextId: d?.default_context_id });
            setChooseIdentity(next ?? null);
          } catch {
            setChooseIdentity(null); // 刷新失敗：至少不帶舊身分，讓首頁自己重抓
          }
        }
        rememberPreferredContext(data.context_id);
        navigate("/home", { replace: true });
      },
      errorCallback: (error: any) => {
        setSubmitting(false);
        // 已註冊的 LINE 帳號又開序號連結：不是錯誤，直接回首頁
        if (error?.response?.data?.message === ALREADY_REGISTERED_MSG) {
          navigate("/home", { replace: true });
          return;
        }
        toast.error(toToastMessage(error?.response?.data?.message, "註冊失敗，請稍後再試"));
      },
    });
  };

  const onSubmit = async (data: FormData) => {
    if (submitting) return;
    // 登入判斷還沒結束：不送（按鈕也是停用的）。判斷一結束，下方自動送出 effect 會接手。
    if (!authReady) return;
    if (lineAccessToken) {
      setSubmitting(true);
      track("funnel_step", { funnel: "register", step: "serial_submit" });
      handleRequest({
        asyncRequest: () =>
          api.post(
            "/app-api/line/authentication-validation",
            {
              accessToken: lineAccessToken,
              authenticationCode: data?.invCode || "",
            },
            { isAuth: false },
          ),
        successCallback: (res: AxiosResponse) => {
          setInvGasData(res.data.data);
          registerAndGoHome(res.data.data?.verifiedTicket);
        },
        errorCallback: (error: any) => {
          setSubmitting(false);
          const { response } = error;
          if (error?.status === 404 && response?.data?.data?.contactUrl) {
            setLoading(true);
            window.location.href = response?.data?.data?.contactUrl || "";
          } else {
            setError("invCode", {
              type: "manual",
              message: "找不到此序號,請確認後重試",
            });
          }
        },
        setLoading,
      });
    } else if (!liff.isInClient()) {
      // 外部瀏覽器才需要導去登入。
      liff.login();
    } else {
      // LINE 內開啟本來就已登入、liff.login() 不會有任何反應；token 只是 LIFF 初始化還沒跑完，
      // 等它到了由下方自動送出的 effect 補送。只有手動按「下一步」會走到這裡，給個回應免得像沒反應。
      setError("invCode", { type: "manual", message: "LINE 登入中，請稍候再按一次" });
    }
  };

  //@ 3. thisInviteCode 一旦設定（來自 URL/storage/cookie 或 /check resume）→ 帶入表單欄位
  useEffect(() => {
    if (thisInviteCode) {
      setValue("invCode", thisInviteCode);
    }
  }, [thisInviteCode, setValue]);

  //@ 4. 表單欄位達 6 碼、已拿到 LINE token、而且 Provider 的登入判斷已結束 → 自動 submit
  //   LINE 重開頁面時本頁會比 LIFF 初始化先渲染：序號（連結帶入／手打）可能在 token 到之前就湊滿 6 碼。
  //   若只看 invCode，那次送出會因沒 token 落空，token 到了序號沒變也不會再送 → 畫面停住
  //   （2026-10-13 prod GS_21 客戶實見）。所以 token 也列入依賴，到了再補送。
  //   authReady 同理（2026-10-15）：要等 /line/login 有結果才送，避免與它並行（見上方 authReady 註解）。
  //   authReady 後仍有 jwt＝Provider 判定這個 LINE 帳號已註冊（401 會清掉 jwt）：由 Provider 導去
  //   加帳戶序號頁或送回 OA，本頁不自動送出（手動按「下一步」仍可，後端會回「已經註冊過」→ 回首頁）。
  const invCode = useWatch({ control, name: "invCode" });
  useEffect(() => {
    if (!lineAccessToken || !authReady) return;
    // 練習模式 Provider 一律塞假 jwt、且不會做 serial_redirect，所以不能以 jwt 擋自動送出
    if (cusJwtToken && !isPracticeMode()) return;
    if (invCode?.trim().length === 6) {
      handleSubmit(onSubmit)();
    }
  }, [invCode, lineAccessToken, authReady]);
  const canSubmit = invCode?.trim().length === 6 && !submitting && authReady;
  // 序號已滿 6 碼、正在等登入判斷：也顯示「驗證中…」（冷開機時可能 1～4 秒）
  const busy = submitting || (!authReady && invCode?.trim().length === 6);

  // 練習模式：正常綁定前模擬「相機掃描客戶序號 QR」。整頁都是鏡頭、中間一個取景框、無 header；
  // 不開真實相機（ScanViewport 不給 onDecode 只顯示取景框＋掃描線動畫），約 2 秒自動辨識完成。
  // 用 fixed 全螢幕 portal 蓋掉 /register 外框 header，做成「整頁都是鏡頭」的感覺。
  if (showScan) {
    return createPortal(
      <div style={{ position: "fixed", inset: 0, zIndex: 100000, background: "#000" }}>
        <YStack width="100%" height="100%" position="relative">
          <ScanViewport fill>
            {/* 疊在鏡頭上：底部「掃描中…」+ 略過 */}
            <div className="absolute left-0 right-0 bottom-10 flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 bg-black/55 rounded-full px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-[#7fc0ff] animate-pulse" />
                <span className="text-[13px] text-white font-semibold">掃描中…</span>
              </div>
              <button
                type="button"
                onClick={() => setScanDone(true)}
                style={{ background: "none", border: "none", color: "rgba(255,255,255,0.8)", fontSize: 13, textDecoration: "underline", cursor: "pointer" }}
              >
                掃不到？直接輸入序號
              </button>
            </div>
          </ScanViewport>
        </YStack>
      </div>,
      document.body,
    );
  }

  return (
    <>
      <YStack {...containerStyle}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <YStack gap={15}>
            <RadiusCard>
              <RadiusBoxLabel label="客戶序號" icon={<RiCoupon2Line color={colors.primary} />} />
              <YStack mt="$3" alignItems="center">
                <Text fontSize={14} fontWeight="bold" color={colors.gray} mb="$3" alignSelf="flex-start">
                  請輸入瓦斯行提供的序號
                </Text>
                <Controller
                  control={control}
                  name="invCode"
                  render={({ field: { onChange, value } }) => (
                    <OTPInputBlocks value={value || ""} onChange={onChange} digits={6} error={errors.invCode?.message} />
                  )}
                />
                {errors.invCode && (
                  <Text color="$red10" fontSize={14} mt="$2" alignSelf="center">
                    {errors.invCode.message}
                  </Text>
                )}
              </YStack>
            </RadiusCard>
            <RadiusCard bg="rgba(255, 255, 255, 0.3)" border="#e2e8f0" borderRadius={10}>
              <Text fontSize={14} color={colors.gray}>
                💡 序號可向您合作的瓦斯行索取
              </Text>
            </RadiusCard>
          </YStack>
          {/* 防呆：滿 6 碼仍會自動送出，按鈕是給「自動送出沒動」時的明確出口；未滿 6 碼或送出中停用。 */}
          <XStack
            className={canSubmit ? "primary-bg-gradient" : undefined}
            backgroundColor={canSubmit ? undefined : "#E2E8F0"}
            borderRadius={20}
            p={15}
            mt={15}
            alignItems="center"
            justifyContent="center"
            cursor={canSubmit ? "pointer" : "default"}
            aria-disabled={!canSubmit}
            onPress={canSubmit ? handleSubmit(onSubmit) : undefined}
          >
            <Text color={canSubmit ? "white" : colors.sub} fontSize={16} fontWeight="bold">
              {busy ? "驗證中…" : "下一步"}
            </Text>
          </XStack>
        </Form>
      </YStack>
    </>
  );
};

export default InvitedCode;
